/**
 * Robot Manager Import 工具 API 客户端（M1 — PO importer）
 * 对应 backend: docs/modules/robot-manager/14-import-export-tool-prd.md §6
 */
import { get, post } from '@/lib/api-client';

/**
 * 必须与 backend `TYPE_MAP`（src/modules/robot-manager/import/import.controller.ts）保持同步。
 * M2 抽 importer framework 时统一从 @prisma/client ImportBatchType 派生，目前手抄。
 */
export type ImportType =
  | 'purchase-order'
  | 'robot-unit'
  | 'master-model'
  | 'master-sku'
  | 'master-customer'
  | 'master-supplier'
  | 'master-location'
  | 'service-ticket';

export type ImportBatchStatus =
  | 'PENDING'
  | 'VALIDATING'
  | 'VALIDATED'
  | 'IMPORTING'
  | 'COMPLETED'
  | 'FAILED'
  | 'SUPERSEDED';

export interface ImportSummary {
  totalRows: number;
  successRows: number;
  errorRows: number;
  warningRows: number;
  templateSchemaHash: string;
  fileHash: string;
  status: ImportBatchStatus;
  deduped?: boolean;
}

/** 与 backend ValidationIssue 同结构（importer.interface.ts） */
export interface ValidationIssue {
  rowNo: number;
  field: string;
  code: string;
  params: Record<string, string>;
  severity: 'ERROR' | 'WARNING';
}

/**
 * Backend 一并返回 top-50 ERROR 行明细，省一次 RTT。
 * 与 backend `ErrorPreviewEntry`（import-batch.service.ts）同名同结构。
 */
export interface ErrorPreviewEntry {
  rowNo: number;
  errorDetail: ValidationIssue[];
}

export interface PreviewResponse {
  batchId: string;
  summary: ImportSummary;
  errorPreview: ErrorPreviewEntry[];
}

export interface ConfirmResponse {
  batchId: string;
  status: ImportBatchStatus;
  completedAt: string;
  successRows: number;
  sideEffects?: { recordCount: number; notes: string };
}

export const robotImportApi = {
  /** 下载模板（Excel） — backend 动态生成；`locale=zh|en` */
  async downloadTemplate(type: ImportType, locale: 'zh' | 'en' = 'zh'): Promise<Blob> {
    return get<Blob>(`/robot-manager/import/${type}/template`, {
      params: { locale },
      responseType: 'blob',
    });
  },

  /** 上传 Excel → preview 校验报告 + 创建 VALIDATED batch */
  async preview(type: ImportType, file: File): Promise<PreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return post<PreviewResponse>(`/robot-manager/import/${type}/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** 确认执行 → all-or-nothing 写库 */
  async confirm(batchId: string): Promise<ConfirmResponse> {
    return post<ConfirmResponse>(`/robot-manager/import/batches/${batchId}/confirm`);
  },

  /** 下载错误报告 Excel */
  async downloadErrorReport(batchId: string): Promise<Blob> {
    return get<Blob>(`/robot-manager/import/batches/${batchId}/error-report.xlsx`, {
      responseType: 'blob',
    });
  },
};
